// Onglet « Règles métier ».
//
// C'est ici qu'atterrit ce que Charles dit au programme : « sur l'inox,
// compte toujours 20 % de temps de cycle en plus ». Au palier 2, le chat
// écrira au même endroit, par les mêmes routes.
//
// Deux choses que cet écran doit rendre impossibles, et qui viennent
// directement de l'audit AGC :
//
//  - **perdre la phrase d'origine.** Chez AGC, l'utilisateur écrivait
//    une règle, une IA la réécrivait, et c'est la réécriture qui était
//    stockée et appliquée. La dérive de sens était invisible. Ici la
//    phrase exacte est immuable et toujours affichée à côté de sa forme
//    normalisée ;
//  - **appliquer sans validation.** Une règle écrite n'agit sur rien.
//    Elle n'entre en vigueur que quand un humain a validé ce qu'il
//    voyait à l'écran.

import { useCallback, useState } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import { TextField, Textarea } from '../../ui/TextField.jsx';
import Button from '../../ui/Button.jsx';
import Badge from '../../ui/Badge.jsx';
import { ErrorState, SkeletonRows, EmptyState } from '../../ui/States.jsx';
import { useToast } from '../../ui/Toast.jsx';
import { useResource } from '../../lib/useResource.js';
import { api } from '../../lib/api.js';
import { useSession } from '../../lib/session.jsx';
import { Link } from 'react-router';
import { decrireEffet } from '@shared/effets.js';

const EMPTY = { scope: '', input_text: '' };

function RuleBadge({ rule }) {
  if (rule.superseded_by) return <Badge tone="neutral">Remplacée</Badge>;
  if (!rule.validated_at) return <Badge tone="warn">À valider</Badge>;
  if (!rule.active) return <Badge tone="neutral">Désactivée</Badge>;
  return <Badge tone="ok">En vigueur</Badge>;
}

const NOMS_AGENTS = {
  estimation: 'estimation', decoupage_consignes: 'découpage des consignes', analyste: 'analyste',
  structure: 'structure',
};

// Depuis le 14 octobre 2026 (assistant, étape 2) : tout le monde ÉCRIT une
// règle — elle naît inactive —, seule la PERMISSION la valide, la corrige ou
// la désactive. Ce n'est plus le rôle admin.
export function ReglesPanel() {
  const { user } = useSession();
  const peutValider = user?.peut_valider_regles === true;
  const toast = useToast();
  const load = useCallback(() => api.get('/regles'), []);
  const { data, error, loading, reload } = useResource(load);
  const [draft, setDraft] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [pending, setPending] = useState(false);

  if (loading) return <SkeletonRows rows={4} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const rules = data.rules ?? [];

  async function call(fn) {
    setPending(true);
    try {
      await fn();
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPending(false);
    }
  }

  const submit = () =>
    call(async () => {
      const path = editing ? `/regles/${editing}/version` : '/regles';
      await api.post(path, { scope: draft.scope, input_text: draft.input_text, structured: null });
      toast.success(
        editing
          ? "Nouvelle version créée. L'ancienne reste consultable."
          : 'Règle enregistrée. Elle doit être validée pour agir.',
      );
      setDraft(EMPTY);
      setEditing(null);
    });

  return (
    <>
      {!editing || peutValider ? (
        <Card>
          <CardHead title={editing ? 'Nouvelle version de la règle' : 'Écrire une règle'}>
            {editing ? (
              <Button size="sm" variant="ghost" onClick={() => { setEditing(null); setDraft(EMPTY); }}>
                Annuler
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="primary"
              disabled={pending || !draft.scope.trim() || !draft.input_text.trim()}
              onClick={submit}
            >
              Enregistrer
            </Button>
          </CardHead>
          <CardBody className="stack">
            <TextField
              label="Portée"
              value={draft.scope}
              hint="Ce que la règle concerne : « global », ou clé:valeur — matiere, famille, forme, client, operation, machine, qte_min, qte_max. Par exemple « matiere:inox 316 » ou « client:precicarb,qte_min:50 »."
              onChange={(e) => setDraft({ ...draft, scope: e.target.value })}
            />
            <Textarea
              label="La règle, dans vos mots"
              value={draft.input_text}
              rows={3}
              hint="Écrivez-la comme vous la diriez. Cette phrase est conservée telle quelle, pour toujours : c'est elle qui fait foi, pas une reformulation."
              onChange={(e) => setDraft({ ...draft, input_text: e.target.value })}
            />
            <p className="field__hint">
              Une règle enregistrée n’agit sur rien tant qu’elle n’a pas été validée, et elle ne
              s’appliquera qu’aux devis <strong>à venir</strong> : aucun devis déjà établi n’est
              rejoué, chacun garde le calcul de son époque.
            </p>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHead title="Règles" />
        <CardBody className="stack">
          {rules.length === 0 ? (
            <EmptyState title="Aucune règle">
              Rien n’a encore été écrit. Les temps proposés ne dépendront que des plans et de
              l’historique.
            </EmptyState>
          ) : (
            rules.map((rule) => (
              <div className="op-row" key={rule.id}>
                <div className="stack">
                  <div className="row">
                    <RuleBadge rule={rule} />
                    <span className="caps">{rule.scope}</span>
                  </div>

                  {/* La phrase d'origine, toujours visible, jamais réécrite. */}
                  <p>{rule.input_text}</p>
                  {rule.structured?.reformulation ? (
                    <p className="field__hint">Reformulation validée : {rule.structured.reformulation}</p>
                  ) : null}
                  <p className="field__hint">
                    {`Agents : ${(rule.agents ?? ['estimation']).map((a) => NOMS_AGENTS[a] ?? a).join(', ')}`}
                    {` · ${rule.nature === 'effet' ? `effet chiffré : ${rule.effet ? decrireEffet(rule.effet) : '?'}` : 'texte'}`}
                    {rule.origine ? ` · née ${rule.origine === 'assistant' ? 'de l’assistant' : `de ${rule.origine}`}` : ''}
                    {rule.conversation_id ? (
                      <> · <Link to={`/assistant?conversation=${rule.conversation_id}`}>voir la conversation</Link></>
                    ) : null}
                  </p>

                  <p className="field__hint">
                    {rule.structured
                      ? 'Forme normalisée enregistrée, injectée dans la proposition de temps.'
                      : "Pas encore de forme normalisée : la règle est conservée mais n'est pas encore exploitable par l'agent."}
                  </p>

                  <p className="field__hint">
                    Écrite par {rule.created_by_name ?? 'un compte supprimé'} le{' '}
                    {new Date(rule.created_at).toLocaleDateString('fr-BE')}
                    {rule.validated_at
                      ? ` — validée par ${rule.validated_by_name ?? 'un compte supprimé'}`
                      : ''}
                    {rule.superseded_by ? ` — remplacée par la règle n° ${rule.superseded_by}` : ''}
                  </p>
                </div>

                {peutValider && !rule.superseded_by ? (
                  <div className="op-row__side">
                    {!rule.validated_at ? (
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={pending}
                        onClick={() =>
                          call(async () => {
                            await api.post(`/regles/${rule.id}/validation`, {});
                            toast.success('Règle validée : elle est en vigueur.');
                          })
                        }
                      >
                        Valider
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={pending}
                        onClick={() =>
                          call(async () => {
                            await api.post(`/regles/${rule.id}/activation`, { active: !rule.active });
                            toast.success(rule.active ? 'Règle désactivée.' : 'Règle réactivée.');
                          })
                        }
                      >
                        {rule.active ? 'Désactiver' : 'Réactiver'}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={pending}
                      onClick={() => {
                        setEditing(rule.id);
                        setDraft({ scope: rule.scope, input_text: rule.input_text });
                      }}
                    >
                      Corriger
                    </Button>
                  </div>
                ) : null}
              </div>
            ))
          )}
        </CardBody>
      </Card>
    </>
  );
}

export default ReglesPanel;
